import { FC, useMemo } from "react";
import styled from "@emotion/styled";
import { MdsIconKind } from "@/design-system/components/icon/types";
import { IntegrationsSection } from "@/pages/integrations/integrations-section";
import { IntegrationItem } from "@/pages/integrations/integrations-page.types";
import { AppStore, useAppStore } from "@/store";
import { observer } from "mobx-react-lite";

export const IntegrationsPageList: FC = observer(() => {
  const { store } = useAppStore();
  const { groups } = useIntegrations({ store });

  return (
    <PageContainer>
      {groups.map(group => (
        <IntegrationsSection key={group.title} title={group.title} items={group.items} />
      ))}
    </PageContainer>
  );
});

const useIntegrations = ({ store }: { store: AppStore }) => {
  const isChromeExtensionInstalled = !!store.chromeExtension.isInstalled.data;

  const groups = useMemo(() => {
    const isTextMessageCaptureFeatureEnabled =
      store.spaceAccountFeatureFlags.getFlagStatus("text-mem");

    const textMessages = store.textMessageSenders.verifiedItems;
    const zapierIntegrationIsEnabled = store.zapierIntegration.isEnabled;

    // Data source for integration items
    const integrationItems: IntegrationItem[] = [
      {
        type: "email",
        title: "Email",
        description: "Create or edit notes from emails you forward to Mem",
        isEnabled: true,
        isAllowed: true,
        icon: MdsIconKind.Envelope,
        onPress: () => store.navigation.goToIntegrationsEmail(),
      },
      {
        type: "sms",
        title: "SMS & WhatsApp",
        description: "Create or edit notes from texts you send to Mem",
        isEnabled: isTextMessageCaptureFeatureEnabled && textMessages.length > 0,
        isAllowed: isTextMessageCaptureFeatureEnabled,
        icon: MdsIconKind.Message,
        onPress: () => store.navigation.goToIntegrationsTextMessage(),
      },
      {
        type: "chrome-extension",
        title: "Chrome Extension",
        description: "Save webpages to Mem from Chrome",
        isEnabled: isChromeExtensionInstalled,
        isAllowed: true,
        icon: MdsIconKind.GoogleChrome,
        onPress: () => store.navigation.goToIntegrationsChromeExtension(),
      },
      {
        type: "api",
        title: "API",
        description: "Create notes with custom integrations",
        isEnabled: store.apiKeys.allActiveApiKeys.length > 0,
        isAllowed: true,
        icon: MdsIconKind.Merge,
        onPress: () => store.navigation.goToIntegrationsApi(),
      },
      {
        type: "zapier",
        title: "Zapier",
        description: "Automate note creation from 8,000+ apps",
        /**
         * For now, always enabled.
         * In the future, this will be calculated based on a backend API call.
         */
        isEnabled: zapierIntegrationIsEnabled,
        isAllowed: true,
        icon: MdsIconKind.LogoZapier,
        onPress: () => store.navigation.goToIntegrationsZapier(),
      },
    ];

    /**
     * We filter out any integrations that are marked as hidden.
     */
    const visibleIntegrations = integrationItems.filter(item => item.isHidden !== true);
    const enabledIntegrations = visibleIntegrations.filter(
      item => item.isAllowed && item.isEnabled
    );
    const availableIntegrations = visibleIntegrations.filter(
      item => item.isAllowed && !item.isEnabled
    );
    const comingSoonIntegrations = visibleIntegrations.filter(item => !item.isAllowed);

    return [
      {
        title: "Enabled Integrations",
        items: enabledIntegrations,
      },
      {
        title: "Available Integrations",
        items: availableIntegrations,
      },
      {
        title: "Coming Soon",
        items: comingSoonIntegrations,
      },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    store.apiKeys.allActiveApiKeys,
    store.spaceAccountFeatureFlags.featureFlags,
    store.emailSenders.items,
    store.textMessageSenders.items,
    store.zapierIntegration.isEnabled,
    isChromeExtensionInstalled,
  ]);

  return { groups };
};

const PageContainer = styled.div`
  display: flex;
  max-width: 1064px;
  padding-top: ${({ theme }) => theme.spacing[4]};
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
`;
